import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { Header } from './Header';
import { Footer } from './Footer';
import { RelatedLocations } from './RelatedLocations';
import { LocationFAQ } from './LocationFAQ';
import { SEO } from './SEO';
import { 
  Server, Zap, Shield, Globe, 
  CheckCircle, Network, HardDrive,
  Clock, MapPin, TrendingUp, Database,
  ArrowRight, Cpu, Lock, Gauge,
  Building2, Users, Wifi, BarChart3
} from 'lucide-react';
import { Button } from './ui/button';
import { PricingSection } from './PricingSection';
import { PaymentMethodsSection } from './PaymentMethodsSection';
import { smoothScrollToWithOffset } from '../lib/scroll';

export const CzechiaPage = () => {
  const { t, language } = useLanguage();
  const [visibleStats, setVisibleStats] = useState(false);
  const statsRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisibleStats(true); },
      { threshold: 0.3 }
    );
    if (statsRef.current) observer.observe(statsRef.current);
    return () => observer.disconnect();
  }, []);

  const scrollToPricing = () => smoothScrollToWithOffset('pricing', 100);

  return (
    <div className="min-h-screen bg-white">
      <SEO page="czechia" />
      <Header />
      
      {/* Hero - Deep Red/Crimson Czech theme */}
      <section className="relative py-20 sm:py-28 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-red-900 via-red-800 to-rose-900">
          <div className="absolute inset-0 opacity-15">
            <div className="absolute top-1/4 right-1/4 w-80 h-80 bg-red-500 rounded-full blur-3xl animate-pulse"></div>
            <div className="absolute bottom-1/3 left-1/3 w-72 h-72 bg-rose-400 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }}></div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="text-white">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full text-sm font-semibold mb-6">
                <MapPin className="w-4 h-4" />
                <span>{t('czechia.hero.badge')}</span>
              </div>
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black mb-6 leading-tight">
                {t('czechia.hero.title')}
              </h1>
              <p className="text-xl text-red-100 mb-8 leading-relaxed max-w-lg">
                {t('czechia.hero.subtitle')}
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button onClick={scrollToPricing} className="bg-white text-red-900 hover:bg-red-50 font-bold px-8 py-6 text-lg rounded-xl shadow-2xl">
                  {t('czechia.hero.getStarted')}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                <Button onClick={scrollToPricing} className="bg-transparent border-2 border-white text-white hover:bg-white/10 font-bold px-8 py-6 text-lg rounded-xl">
                  {t('czechia.hero.viewPlans')}
                </Button>
              </div>
            </div>
            <div className="hidden lg:block">
              <div className="rounded-2xl overflow-hidden shadow-2xl">
                <img src="/images/locations/prague-bridge.jpg" alt="Prague Charles Bridge" loading="lazy" className="w-full h-[28rem] object-cover" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Animated Stats Bar */}
      <section ref={statsRef} className="py-12 bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { icon: TrendingUp, value: '99.9%', label: t('czechia.stats.uptimeLabel') },
              { icon: Gauge, value: '1Gbps', label: t('czechia.stats.speedLabel'), wiki: 'shared-1gbps-port-explained' },
              { icon: Clock, value: '<12ms', label: t('czechia.stats.latencyLabel') },
              { icon: Shield, value: 'EU', label: t('czechia.stats.complianceLabel') }
            ].map((stat, i) => (
              <div key={i} className={"text-center transition-all duration-700 " + (visibleStats ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4')} style={{ transitionDelay: i * 150 + 'ms' }}>
                <div className="inline-flex items-center justify-center w-14 h-14 bg-red-100 rounded-xl mb-3">
                  <stat.icon className="w-7 h-7 text-red-700" />
                </div>
                <div className="text-3xl sm:text-4xl font-black text-gray-900">{stat.value}</div>
                <div className="text-sm text-gray-500 mt-1">{stat.label}</div>
                {stat.wiki && (
                  <Link
                    to={(language === 'ru' ? '/ru' : '') + '/wiki/' + stat.wiki}
                    className="mt-1 inline-flex items-center gap-1 text-xs text-red-700 hover:text-red-900 transition-colors"
                  >
                    {language === 'ru' ? 'Что значит общий 1 Гбит/с?' : 'What does shared 1 Gbps mean?'}
                    <ArrowRight className="w-3 h-3" />
                  </Link>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Prague - Staggered Cards */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-4">{t('czechia.why.title')}</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">{t('czechia.why.subtitle')}</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {t('czechia.why.items').map((item, i) => {
              const icons = [Globe, Network, Shield, Building2, Cpu, Lock];
              const Icon = icons[i] || Globe;
              return (
                <div key={i} className={"bg-white rounded-2xl p-8 shadow-lg border border-gray-100 hover:shadow-xl hover:border-red-200 transition-all " + (i === 0 ? 'lg:col-span-2' : '')}>
                  <div className="w-14 h-14 bg-gradient-to-br from-red-600 to-rose-700 rounded-xl flex items-center justify-center mb-5 shadow-lg">
                    <Icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{item.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{item.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Prague Image + Features Split */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <div className="rounded-2xl overflow-hidden shadow-xl">
                <img src="/images/locations/prague-oldtown.jpg" alt="Prague Old Town" loading="lazy" className="w-full h-72 object-cover" />
              </div>
              <div className="grid grid-cols-2 gap-6">
                <div className="rounded-xl overflow-hidden shadow-lg">
                  <img src="/images/locations/prague-castle.jpg" alt="Prague Castle" loading="lazy" className="w-full h-44 object-cover" />
                </div>
                <div className="rounded-xl overflow-hidden shadow-lg">
                  <img src="/images/locations/czech-flag.png" alt="Czech Republic Flag" loading="lazy" className="w-full h-44 object-cover bg-white" />
                </div>
              </div>
            </div>

            <div>
              <h2 className="text-4xl font-black text-gray-900 mb-6">{t('czechia.features.title')}</h2>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">{t('czechia.features.subtitle')}</p>
              
              <div className="space-y-5">
                {t('czechia.features.items').map((item, i) => {
                  const icons = [HardDrive, Zap, Shield, Database, Wifi, BarChart3];
                  const Icon = icons[i] || Server;
                  return (
                    <div key={i} className="flex items-start gap-4">
                      <div className="w-11 h-11 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Icon className="w-5 h-5 text-red-700" />
                      </div>
                      <div>
                        <h3 className="font-bold text-gray-900 mb-1">{item.title}</h3>
                        <p className="text-sm text-gray-600">{item.desc}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related Locations */}
      <RelatedLocations currentLocation="vps-czechia" />

      {/* Pricing */}
      <div id="pricing">
        <PricingSection hideHeader={false} title={t('czechia.pricing.title')} subtitle={t('czechia.pricing.subtitle')} />
      </div>
      <PaymentMethodsSection transparentBg={false} />

      {/* Location Details */}
      <section className="py-20 bg-gradient-to-b from-white to-red-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-4">{t('czechia.location.title')}</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">{t('czechia.location.description')}</p>
          </div>

          <div className="bg-white rounded-2xl p-8 shadow-xl border border-red-100">
            <div className="bg-red-50 border-l-4 border-red-600 p-6 rounded-r-lg mb-8">
              <h3 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                <MapPin className="w-5 h-5 text-red-600" />
                {t('czechia.location.subtitle')}
              </h3>
              <p className="text-gray-700">{t('czechia.location.details')}</p>
            </div>
            
            <div className="grid sm:grid-cols-2 gap-4">
              {t('czechia.location.benefits').map((benefit, i) => (
                <div key={i} className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">{benefit}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-28 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-red-900 via-red-800 to-rose-900"></div>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-1/4 w-96 h-96 bg-red-400 rounded-full blur-3xl"></div>
        </div>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-5xl md:text-6xl font-black text-white mb-6">{t('czechia.cta.title')}</h2>
          <p className="text-xl text-red-100 mb-12 max-w-3xl mx-auto">{t('czechia.cta.subtitle')}</p>
          <Button onClick={scrollToPricing} className="bg-white text-red-900 hover:bg-red-50 font-bold px-16 py-8 text-xl rounded-full shadow-2xl transform hover:scale-105 transition-all">
            {t('czechia.cta.button')}
            <ArrowRight className="w-7 h-7 ml-3" />
          </Button>
        </div>
      </section>

      <LocationFAQ location="vps-czechia" />
      <Footer />
    </div>
  );
};
